import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchLotesPorProyecto } from '@/api/sitePlan';
import type { LoteSVG, ProyectoCode } from '@/types/sitePlan';
import type { SitePlanFilters } from '@/store/appStore';

/**
 * Trae los lotes de un proyecto y los cachea con React Query.
 * Como los lotes solo cambian cuando se sincroniza ArchiCAD, usamos
 * staleTime largo para no refetchear constantemente.
 */
export function useSitePlanLotes(proyecto: ProyectoCode) {
  return useQuery({
    queryKey: ['lotes-svg', proyecto],
    queryFn: ({ signal }) => fetchLotesPorProyecto(proyecto, signal),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Catálogos derivados (valores únicos por columna) del set de lotes
 * del proyecto. Se memoiza para no recalcular en cada render.
 *
 * Los catálogos se construyen sobre TODO el set de lotes del proyecto
 * (no sobre el set ya filtrado), porque la UX típica es: el usuario
 * elige Estado → ve los modelos disponibles, no quedan vacíos.
 *
 * Si más adelante querés "dropdowns dinámicos" (donde el set de
 * Modelos depende del Estado seleccionado, etc.), hay que pasar los
 * filtros activos a este hook y excluir del filtrado el campo cuyo
 * dropdown se está construyendo.
 */
export function useSitePlanCatalogs(proyecto: ProyectoCode) {
  const { data: lotes = [] } = useSitePlanLotes(proyecto);

  return useMemo(() => {
    const estados = new Set<string>();
    const modelos = new Set<string>();
    const vendedores = new Set<string>();
    const bancos = new Set<string>();
    const formalizadores = new Set<string>();
    const frentes = new Set<number>();
    const fondos = new Set<number>();

    for (const l of lotes) {
      if (l.estado) estados.add(l.estado);
      if (l.modeloCaso) modelos.add(l.modeloCaso);
      if (l.modeloTramitado) modelos.add(l.modeloTramitado);
      if (l.vendedor) vendedores.add(l.vendedor);
      if (l.banco) bancos.add(l.banco);
      if (l.formalizador) formalizadores.add(l.formalizador);
      if (l.frente != null) frentes.add(l.frente);
      if (l.fondo != null) fondos.add(l.fondo);
    }

    return {
      estados: [...estados].sort(),
      modelos: [...modelos].sort(),
      vendedores: [...vendedores].sort(),
      bancos: [...bancos].sort(),
      formalizadores: [...formalizadores].sort(),
      frentes: [...frentes].sort((a, b) => a - b),
      fondos: [...fondos].sort((a, b) => a - b),
    };
  }, [lotes]);
}

/**
 * Aplica filtros + búsqueda a un array de lotes. Se usa tanto para mostrar
 * los lotes filtrados como para resaltar selección/atenuar no-coincidencias.
 */
export function applySitePlanFilters(
  lotes: LoteSVG[],
  filters: SitePlanFilters,
  search: string,
): LoteSVG[] {
  const q = search.trim().toLowerCase();
  return lotes.filter((l) => {
    // Dropdowns
    if (filters.estado && l.estado !== filters.estado) return false;
    if (
      filters.modelo &&
      l.modeloCaso !== filters.modelo &&
      l.modeloTramitado !== filters.modelo
    ) return false;
    if (filters.vendedor && l.vendedor !== filters.vendedor) return false;
    if (filters.banco && l.banco !== filters.banco) return false;
    if (filters.formalizador && l.formalizador !== filters.formalizador) return false;
    if (filters.frente !== null && l.frente !== filters.frente) return false;
    if (filters.fondo !== null && l.fondo !== filters.fondo) return false;

    // Toggles
    if (filters.enConstruccion && !l.tgEnConstruccion) return false;
    if (filters.conPermiso && !l.tgConPermiso) return false;

    // Búsqueda libre (también incluye formalizador ahora)
    if (q) {
      const hay =
        l.lote.toLowerCase().includes(q) ||
        (l.cliente?.toLowerCase().includes(q) ?? false) ||
        (l.modeloCaso?.toLowerCase().includes(q) ?? false) ||
        (l.modeloTramitado?.toLowerCase().includes(q) ?? false) ||
        (l.banco?.toLowerCase().includes(q) ?? false) ||
        (l.vendedor?.toLowerCase().includes(q) ?? false) ||
        (l.formalizador?.toLowerCase().includes(q) ?? false);
      if (!hay) return false;
    }

    return true;
  });
}